/**
 * Button Block - Copy to clipboard
 *
 * Key decisions:
 *  - Both handlers are delegated from the document rather than bound per
 *    button, so a button revealed by a modal, slider or AJAX render works
 *    without an init pass and without re-scanning the DOM.
 *  - The trigger carries its own copy text and message in data attributes, so
 *    nothing has to be looked up or passed from PHP.
 *  - Feedback is only shown when the copy actually succeeded. Claiming "Copied!"
 *    over a clipboard write that was blocked is worse than staying silent.
 *  - The tooltip text is written on every copy and cleared after the fade, which
 *    is what lets the live region announce a repeat copy of the same value.
 */

// How long the tooltip stays up before it starts fading out.
const FEEDBACK_DURATION = 2000;
// Must stay in step with the tooltip's opacity transition in style.scss. The
// label is only cleared once it has faded, so the text never blanks mid-fade.
const FADE_DURATION = 200;

const timers = new WeakMap();

/**
 * Write a string to the clipboard.
 *
 * @param {string} text Value to copy.
 * @return {Promise<boolean>} Whether the value reached the clipboard.
 */
async function writeToClipboard(text) {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// Falls through to the legacy path below. The Clipboard API is
			// absent over plain HTTP and can be denied by permissions policy,
			// neither of which is worth failing the whole interaction over.
		}
	}

	const textarea = document.createElement('textarea');
	textarea.value = text;
	textarea.setAttribute('readonly', '');
	// Fixed and transparent so selecting it cannot scroll the page or flash.
	textarea.style.position = 'fixed';
	textarea.style.top = '0';
	textarea.style.opacity = '0';
	document.body.appendChild(textarea);
	textarea.select();

	let copied = false;
	try {
		copied = document.execCommand('copy');
	} catch {
		copied = false;
	}

	document.body.removeChild(textarea);
	return copied;
}

/**
 * Show the confirmation tooltip on a button, then take it away again.
 *
 * @param {HTMLElement} wrapper The block wrapper holding the tooltip.
 * @param {string}      message Text to announce and display.
 */
function showFeedback(wrapper, message) {
	const tooltip = wrapper.querySelector('.brandy-button-copy-tooltip');
	if (!tooltip) {
		return;
	}

	// A second click restarts the countdown rather than inheriting the first
	// one's remaining time, which would cut the new message short.
	const pending = timers.get(tooltip);
	if (pending) {
		clearTimeout(pending.hide);
		clearTimeout(pending.clear);
	}

	tooltip.textContent = message;
	tooltip.classList.add('is-visible');

	timers.set(tooltip, {
		hide: setTimeout(() => {
			tooltip.classList.remove('is-visible');
		}, FEEDBACK_DURATION),
		clear: setTimeout(() => {
			tooltip.textContent = '';
		}, FEEDBACK_DURATION + FADE_DURATION),
	});
}

document.addEventListener('click', async (event) => {
	const trigger = event.target.closest?.('[data-brandy-copy]');
	if (!trigger) {
		return;
	}

	// Copying is the whole point of the click, so a linked button must not also
	// navigate away from the value the visitor just asked for.
	event.preventDefault();

	const wrapper = trigger.closest('.wp-block-brandy-button');
	if (!wrapper) {
		return;
	}

	const copied = await writeToClipboard(
		trigger.getAttribute('data-brandy-copy')
	);

	if (copied) {
		showFeedback(
			wrapper,
			trigger.getAttribute('data-brandy-copy-feedback') || 'Copied!'
		);
	}
});

/**
 * Keyboard activation for a copy button that is an `<a>` without an `href`.
 *
 * save() gives that case `role="button"` and a tab stop, but neither the anchor
 * nor the role brings key handling with it. `<button>` and real links already
 * fire a click of their own and are left alone.
 */
document.addEventListener('keydown', (event) => {
	if (event.key !== 'Enter' && event.key !== ' ') {
		return;
	}

	const trigger = event.target.closest?.('[data-brandy-copy]');
	if (!trigger || trigger.tagName === 'BUTTON' || trigger.hasAttribute('href')) {
		return;
	}

	// Space would otherwise scroll the page out from under the button.
	event.preventDefault();
	trigger.click();
});
